import { useState } from "react";
import logo from "../../assets/img/logo.png";
import logoWhite from "../../assets/img/logo-white.png";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { EyeCloseIcon, EyeIcon } from "../../assets/icons";
import { Formik, Form, Field, ErrorMessage } from "formik";
import axios from "axios";
import toast from "react-hot-toast";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation();
  const apiUrl = import.meta.env.VITE_API_URL;
  const authLoginUri = import.meta.env.VITE_AUTH_LOGIN_API_URI;
  const afterLoginUrl = import.meta.env.VITE_AUTH_AFTER_LOGIN_URL;

  const validationSchema = Yup.object({
    username: Yup.string()
      .max(12, t("validation.max", { max: 12 }))
      .required(t("validation.required")),
    password: Yup.string().required(t("validation.required")),
  });

  const initialValues = {
    username: "",
    password: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const res = await axios.post(apiUrl + authLoginUri, values);

      localStorage.setItem("token", res.data.token);
      window.location.href = afterLoginUrl;
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error(err.response.data?.message || "Invalid credentials");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign in</title>
      </Helmet>

      <div className="relative z-1 flex flex-col p-6">
        <div className="mx-auto w-full max-w-[600px] sm:max-w-[900px]">
          <div className="mt-5 mb-12 w-full">
            <div className="block w-fit mx-auto mb-10 text-center">
              <img src={logo} alt="Logo" className="block dark:hidden" />
              <img src={logoWhite} alt="Logo" className="hidden dark:block" />
            </div>

            <div className="flex flex-col flex-1 w-full max-w-md mx-auto">
              <div>
                <div className="mb-5 sm:mb-8">
                  <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
                    Sign In
                  </h1>
                </div>
                <div>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {(props) => (
                      <Form>
                        <div className="space-y-6">
                          <div>
                            <label
                              htmlFor="username"
                              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                            >
                              Username <span className="text-error-500">*</span>{" "}
                            </label>
                            <Field
                              type="text"
                              name="username"
                              id="username"
                              maxLength={12}
                              autoFocus
                              className={
                                (props.errors.username
                                  ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                                  : "border-gray-300 ") +
                                "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                              }
                            />
                            <ErrorMessage
                              name="username"
                              component="p"
                              className="text-theme-xs text-error-500 mt-1.5"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="password"
                              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400"
                            >
                              Password <span className="text-error-500">*</span>{" "}
                            </label>

                            <div className="relative">
                              <Field
                                type={showPassword ? "text" : "password"}
                                name="password"
                                id="password"
                                maxLength={20}
                                className={
                                  (props.errors.username
                                    ? "border-error-300 focus:border-error-300 focus:ring-error-500/10 dark:border-error-700 dark:focus:border-error-800 "
                                    : "border-gray-300 ") +
                                  "dark:bg-dark-900 shadow-theme-xs focus:border-brand-300 focus:ring-brand-500/10 dark:focus:border-brand-800 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:ring-3 focus:outline-hidden dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                                }
                              />

                              <span
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                              >
                                {showPassword ? (
                                  <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                                ) : (
                                  <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                                )}
                              </span>
                            </div>
                            <ErrorMessage
                              name="password"
                              component="p"
                              className="text-theme-xs text-error-500 mt-1.5"
                            />
                          </div>
                          <div>
                            <button
                              type="submit"
                              disabled={props.isSubmitting}
                              className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                            >
                              {props.isSubmitting
                                ? "Signing in .."
                                : "Sign in"}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
